/* global google */
import html from './template.html'

const template = document.createElement('template')
template.innerHTML = html

export default class MunicipalityPickerMapElement extends HTMLElement {
    static get observedAttributes() {
        return ['center', 'zoom', 'ready']
    }

    /**
     * The default lat and lng coordinates.
     */
    static get defaultCoordinates() {
        return {
            lat: 52.1325623,
            lng: 5.6251025,
        }
    }

    /**
     * The default zoom level.
     */
    static get defaultZoom() {
        return 7
    }

    /**
     * The county layer style.
     */
    static get countyStyle() {
        return {
            strokeColor: '#51277f',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#51277f',
            fillOpacity: 0.1,
        }
    }

    /**
     * The highlighted county layer style.
     */
    static get highlightedCountyStyle() {
        return {
            ...MunicipalityPickerMapElement.countyStyle,
            fillOpacity: 0.8,
        }
    }

    /**
     * The municipality layer style.
     */
    static get municipalityStyle() {
        return {
            strokeColor: '#51277f',
            strokeOpacity: 0.95,
            strokeWeight: 2,
            fillColor: '#fdc500',
            fillOpacity: 0.35,
        }
    }

    /**
     * The inactive municipality style.
     */
    static get inActiveMunicipalityStyle() {
        return {
            ...MunicipalityPickerMapElement.municipalityStyle,
            fillOpacity: 0.1,
        }
    }

    /**
     * The highlighted municipality layer style.
     */
    static get highlightedMunicipalityStyle() {
        return {
            ...MunicipalityPickerMapElement.municipalityStyle,
            fillOpacity: 0.8,
        }
    }

    constructor() {
        super()

        const shadow = this.attachShadow({ mode: 'open' })
        shadow.append(template.content.cloneNode(true))

        this.controlsSlot.addEventListener('slotchange', event => {
            const controls = event.target.assignedElements()
            for (const control of controls) {
                control.addEventListener('click', this.onButtonClick)
            }
        })

        this.map = null
        this.countyLayer = null
        this.municpalityLayer = null

        this.countyFeatures = []
        this.highlightedCountyFeatures = []
        this.municipalityFeatures = []
        this.highlightedMunicipalityFeatures = []

        /**
         * We need to store the MapsEventListener object whenever we add event listeners
         * with google maps. At some point we need to remove the event listeners and for
         * that the references to MapsEventListener objects.
         */
        this.events = {
            map: {},
            county: {},
            municipality: {},
        }

        /**
         * To assure that the correct binding is preserved we bind the "this"
         * value to the internal methods. I'd prefer to use public class fields
         * but couldn't get eslint to cooperate.
         */
        this.createMap = this.createMap.bind(this)
        this.onButtonClick = this.onButtonClick.bind(this)
        this.onCountyClick = this.onCountyClick.bind(this)
        this.onCountyMouseOver = this.onCountyMouseOver.bind(this)
        this.onCountyMouseOut = this.onCountyMouseOut.bind(this)
        this.onMunicipalityClick = this.onMunicipalityClick.bind(this)
        this.onMunicipalityMouseOver = this.onMunicipalityMouseOver.bind(this)
        this.onMunicipalityMouseOut = this.onMunicipalityMouseOut.bind(this)
    }

    /**
     * Returns the map element from the Shadow DOM
     * @returns {HTMLElement}
     */
    get mapElement() {
        return this.shadowRoot.querySelector('.map')
    }

    get controlsSlot() {
        return this.shadowRoot.querySelector('slot[name="controls"]')
    }

    /**
     * Gets and sets the ready attribute value.
     */
    get ready() {
        return this.hasAttribute('ready')
    }

    set ready(value) {
        if (value === true) {
            this.setAttribute('ready', '')
        } else {
            this.removeAttribute('ready')
        }
    }

    /**
     * Gets and sets the controls-active attribute value.
     */
    get controlsActive() {
        return this.hasAttribute('controls-active')
    }

    set controlsActive(value) {
        if (value === true) {
            this.setAttribute('controls-active', '')
        } else {
            this.removeAttribute('controls-active')
        }
    }

    /**
     * Gets and sets the center attribute value.
     */
    get center() {
        const coordinates = this.getAttribute('center')
        if (coordinates === null) {
            return MunicipalityPickerMapElement.defaultCoordinates
        }

        const [lat, lng] = coordinates.split(',')

        return {
            lat: parseFloat(lat),
            lng: parseFloat(lng),
        }
    }

    set center(value) {
        if (typeof value === 'string') {
            this.setAttribute('center', value)
        } else if (typeof value === 'object') {
            if ('lat' in value && 'lng' in value) {
                const newValue = `${value.lat},${value.lng}`
                this.setAttribute('center', newValue)
            }
        }
    }

    /**
     * Gets and sets the zoom attribute value.
     */
    get zoom() {
        const zoom = this.getAttribute('zoom')
        if (zoom === null) {
            return MunicipalityPickerMapElement.defaultZoom
        }

        return parseFloat(zoom)
    }

    set zoom(value) {
        if (typeof value === 'number') {
            this.setAttribute('zoom', value)
        }
    }

    /**
     * Callback that listens for changes in the observed attributes.
     * @param {string} attrName The name of the attribute that has been changed.
     */
    attributeChangedCallback(attrName) {
        switch (attrName) {
            case 'ready':
                if (this.ready === true) {
                    this.dispatchMunicipalityMapReady()
                }
                break

            case 'center':
                if (this.ready === true) {
                    this.map.setCenter(this.center)
                }
                break

            case 'zoom':
                if (this.ready === true) {
                    this.map.setZoom(this.zoom)
                }
                break
        }
    }

    /**
     * Start listening to the event listeners.
     * @returns {void}
     */
    connectedCallback() {
        window.addEventListener('googlemapsapiready', this.createMap)
    }

    /**
     * Remove the event listeners whenever the element is removed.
     * @returns {void}
     */
    disconnectedCallback() {
        window.removeEventListener('googlemapsapiready', this.createMap)
    }

    /**
     * Dispatch the municipalitymapready on this element.
     * @returns {void}
     */
    dispatchMunicipalityMapReady() {
        this.dispatchEvent(
            new CustomEvent('municipalitymapready', {
                bubbles: true,
            })
        )
    }

    /**
     * Dispatch the countyhighlight on this element.
     * @param {string} statcode
     * @returns {void}
     */
    dispatchCountyHighlight(statcode = null) {
        this.dispatchEvent(
            new CustomEvent('countyhighlight', {
                bubbles: true,
                detail: {
                    statcode,
                },
            })
        )
    }

    /**
     * Dispatch the countychange on this element.
     * @param {string} [statcode=null]
     * @returns {void}
     */
    dispatchCountyChange(statcode = null) {
        this.dispatchEvent(
            new CustomEvent('countychange', {
                bubbles: true,
                detail: {
                    statcode,
                },
            })
        )
    }

    /**
     * Dispatch the countychange on this element.
     * @param {string} statcode
     * @returns {void}
     */
    dispatchMunicipalityHighlight(statcode = null) {
        this.dispatchEvent(
            new CustomEvent('municipalityhighlight', {
                bubbles: true,
                detail: {
                    statcode,
                },
            })
        )
    }

    /**
     * Dispatch the countychange on this element.
     * @param {string} statcode
     * @returns {void}
     */
    dispatchMunicipalityClick(statcode) {
        this.dispatchEvent(
            new CustomEvent('municipalityclick', {
                bubbles: true,
                detail: {
                    statcode,
                },
            })
        )
    }

    /**
     * Creates a new google map instance.
     * @returns {void}
     */
    createMap() {
        this.map = new google.maps.Map(this.mapElement, {
            center: this.center,
            zoom: this.zoom,
            mapId: 'fa555fc6422280e0',
            mapTypeControl: false,
            panControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            zoomControl: false,
        })

        this.events.map['click'] = this.map.addListener('click', () => {
            this.dispatchCountyChange()
        })

        this.countyLayer = new google.maps.Data({
            map: this.map,
            style: MunicipalityPickerMapElement.countyStyle,
        })

        this.municpalityLayer = new google.maps.Data({
            map: this.map,
            style: MunicipalityPickerMapElement.municipalityStyle,
        })

        /**
         * We call this so that we can later remove the events if needed.
         */
        this.addCountyLayerEvents()
        this.addMunicipalityLayerEvents()

        // Let it be know that the map is ready.
        this.ready = true
    }

    /**
     * Add event listeners to the county layer.
     * Store the references to the MapsEventListener objects.
     * @returns {void}
     */
    addCountyLayerEvents() {
        this.events.county['click'] = this.countyLayer.addListener(
            'click',
            this.onCountyClick
        )
        this.events.county['mouseover'] = this.countyLayer.addListener(
            'mouseover',
            this.onCountyMouseOver
        )
        this.events.county['mouseout'] = this.countyLayer.addListener(
            'mouseout',
            this.onCountyMouseOut
        )
    }

    /**
     * Remove the events from the county layer.
     * @returns {void}
     */
    removeCountyLayerEvents() {
        for (const event of Object.values(this.events.county)) {
            google.maps.event.removeListener(event)
        }
    }

    /**
     * Add event listeners to the municipality layer.
     * Store the references to the MapsEventListener objects.
     * @returns {void}
     */
    addMunicipalityLayerEvents() {
        this.events.municipality['click'] = this.municpalityLayer.addListener(
            'click',
            this.onMunicipalityClick
        )
        this.events.municipality['mouseover'] =
            this.municpalityLayer.addListener(
                'mouseover',
                this.onMunicipalityMouseOver
            )
        this.events.municipality['mouseout'] =
            this.municpalityLayer.addListener(
                'mouseout',
                this.onMunicipalityMouseOut
            )
    }

    /**
     * Remove the events from the municipality layer.
     * @returns {void}
     */
    removeMunicipalityLayerEvents() {
        for (const event of Object.values(this.events.municipality)) {
            google.maps.event.removeListener(event)
        }
    }

    /**
     * Slot button click event handler.
     * @param {Event} event
     */
    onButtonClick(event) {
        this.dispatchCountyChange()
        event.preventDefault()
    }

    /**
     * County click event handler.
     * @param {Event} event
     */
    onCountyClick({ feature }) {
        const statcode = feature.getProperty('statcode')
        this.dispatchCountyChange(statcode)
    }

    /**
     * County mouseover event handler.
     * @param {Event} event
     */
    onCountyMouseOver({ feature }) {
        const statcode = feature.getProperty('statcode')
        this.dispatchCountyHighlight(statcode)
    }

    /**
     * County mouseout event handler.
     * @param {Event} event
     */
    onCountyMouseOut() {
        this.dispatchCountyHighlight()
    }

    /**
     * Municipality click event handler.
     * @param {Event} event
     */
    onMunicipalityClick({ feature }) {
        const statcode = feature.getProperty('statcode')
        this.dispatchMunicipalityClick(statcode)
    }

    /**
     * Municipality mouseover event handler.
     * @param {Event} event
     */
    onMunicipalityMouseOver({ feature }) {
        const statcode = feature.getProperty('statcode')
        this.dispatchMunicipalityHighlight(statcode)
    }

    /**
     * Municipality mouseout event handler.
     * @param {Event} event
     */
    onMunicipalityMouseOut() {
        this.dispatchMunicipalityHighlight()
    }

    /**
     * Creates a bounds object and moves the map to a position and zoom level to fit the bounds.
     * @returns {void}
     */
    centerOnCountyFeatures() {
        const bounds = new google.maps.LatLngBounds()

        for (const feature of this.countyFeatures) {
            const geometry = feature.getGeometry()
            geometry.forEachLatLng(latLng => bounds.extend(latLng))
        }

        if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds)
        }
    }

    /**
     * Renders GeoJSON data on the county layer.
     * @param {object} geoJson A GeoJSON format object.
     */
    renderCountyGeoJSON(geoJson) {
        const features = this.countyLayer.addGeoJson(geoJson)
        this.countyFeatures = [...this.countyFeatures, ...features]
    }

    /**
     * Clear all the features on the county layer.
     * @returns {void}
     */
    clearCountyFeatures() {
        for (const feature of this.countyFeatures) {
            this.countyLayer.remove(feature)
        }
        this.countyFeatures.length = 0
    }

    /**
     * Applies a highlighted style to a specific feature based on the provided statcode.
     * @param {string} highlightedFeatureStatcode Statcode of the feature to highlight.
     */
    highlightedCountyFeature(highlightedFeatureStatcode) {
        const feature = this.countyFeatures.find(feature => {
            const statcode = feature.getProperty('statcode')
            return statcode === highlightedFeatureStatcode
        })

        if (feature === undefined) {
            return
        }

        this.countyLayer.overrideStyle(
            feature,
            MunicipalityPickerMapElement.highlightedCountyStyle
        )
    }

    /**
     * Disables the highlighted styles from the county layer.
     * @returns {void}
     */
    clearCountyHighlights() {
        this.countyLayer.revertStyle()
    }

    /**
     * Loops over an array of features and styles them according to their active property.
     * @param {google.maps.Data.Feature[]} features
     */
    styleInActiveMunicipalities(features) {
        for (const feature of features) {
            const isActive = feature.getProperty('active')
            if (!isActive) {
                this.municpalityLayer.overrideStyle(
                    feature,
                    MunicipalityPickerMapElement.inActiveMunicipalityStyle
                )
            }
        }
    }

    /**
     * Renders GeoJSON data on the municipality layer.
     * @param {object} geoJson A GeoJSON format object.
     */
    renderMuniciPalityGeoJSON(geoJson) {
        const features = this.municpalityLayer.addGeoJson(geoJson)
        this.municipalityFeatures = [...this.municipalityFeatures, ...features]
        this.styleInActiveMunicipalities(features)
    }

    /**
     * Clear all the features on the municipality layer.
     * @returns {void}
     */
    clearMunicipalityFeatures() {
        for (const feature of this.municipalityFeatures) {
            this.municpalityLayer.remove(feature)
        }
        this.municipalityFeatures.length = 0
    }

    /**
     * Applies a highlighted style to a specific feature based on the provided statcode.
     * @param {string} highlightedFeatureStatcode Statcode of the feature to highlight.
     */
    highlightedMunicipalityFeature(highlightedFeatureStatcode) {
        const feature = this.municipalityFeatures.find(feature => {
            const statcode = feature.getProperty('statcode')
            return statcode === highlightedFeatureStatcode
        })

        if (feature === undefined) {
            return
        }

        this.municpalityLayer.overrideStyle(
            feature,
            MunicipalityPickerMapElement.highlightedMunicipalityStyle
        )
    }

    /**
     * Disables the highlighted styles from the municipality layer.
     * Then reset the "inactive" style of certain municipality features.
     * @returns {void}
     */
    clearMunicipalityHighlights() {
        this.municpalityLayer.revertStyle()
        this.styleInActiveMunicipalities(this.municipalityFeatures)
    }
}

customElements.define('municipality-picker-map', MunicipalityPickerMapElement)



// WEBPACK FOOTER //
// ./scripts/web-components/municipality-picker-map/index.js